import { useQuery } from "@tanstack/vue-query";
import { computed, toValue, watch } from "vue";
import { useToast } from "vue-toast-notification";
import { useStore } from "vuex";
import { useCustomPriceFees } from "@/composables/authorization.js";
import {
  APPLICATION_FEE_DATA,
  CONFIRMATION_PAYMENT_PRICE_IN_CENTS,
  CUSTOMIZED_FEE_FETCH_ISSUE,
  ERROR_TIMEOUT,
  PROGRAM_STEP_DISABLED_STATUSES,
} from "@/constants";

import { centsToDollars, dollarsToCents } from "@/mixins/helpers";
import programSessionService from "@/services/programSessions";
import { isValidUuid } from "@/util/uuid";

const SESSION_FEES_QUERY_KEY = "fetchSessionForFees";

/** centsToDollars runs the value through parseFloat, which turns an unknown amount into NaN. */
const toDollars = (cents) => (cents === null || cents === undefined ? null : centsToDollars(cents));

export const useSessionFees = (sessionId = "", order = {}, sessionOverride) => {
  const store = useStore();
  const toast = useToast();

  const storeProgram = computed(() => store.getters["configurator/getCurrentProgram"]);
  const resolvedOrder = computed(() => toValue(order) ?? null);
  const providedSession = computed(() => toValue(sessionOverride) ?? storeProgram.value?.programSession ?? null);

  const grabPricesFromOrder = computed(() => PROGRAM_STEP_DISABLED_STATUSES.includes(resolvedOrder.value?.status));

  const sessionFetchIsEnabled = computed(() => {
    if (!toValue(useCustomPriceFees)) {
      return false;
    }
    // The id goes into the request path, where the service requires a UUID. The session CMS
    // reuses this param slot for a channel type while creating ("1" Core, "2" Custom,
    // "3" Adopted), so a truthiness check alone sends "/programming/sessions/1" and earns a 422
    // — retried three times and surfaced through the global error bus.
    if (!isValidUuid(toValue(sessionId))) {
      return false;
    }
    // The caller already holds the session, so there is nothing to fetch. Checking the resolved
    // value rather than the presence of the argument keeps `loading` and `error` meaningful for
    // callers whose session arrives asynchronously.
    if (providedSession.value) {
      return false;
    }
    return true;
  });

  const {
    isLoading: isLoadingSession,
    isError: isSessionError,
    data: sessionData,
  } = useQuery({
    queryKey: computed(() => [SESSION_FEES_QUERY_KEY, toValue(sessionId)]),
    queryFn: async () => {
      return programSessionService.getProgramSession({
        id: toValue(sessionId),
      });
    },
    enabled: sessionFetchIsEnabled,
    refetchOnWindowFocus: false,
  });

  const session = computed(() => providedSession.value ?? sessionData.value?.data?.data?.items ?? null);

  /**
   * Object the amounts must be read from: the order once it is submitted, otherwise the session.
   * Null until that object is available, which is what separates "not loaded yet" from "$0".
   */
  const feeSource = computed(() => (grabPricesFromOrder.value ? resolvedOrder.value : session.value));

  const feesAreResolved = computed(() => !useCustomPriceFees.value || Boolean(feeSource.value));

  /**
   * The amounts are only still on their way while a fetch that can supply them is outstanding.
   * Once that fetch errors, or was never enabled, the fees are as resolved as they will ever get,
   * so callers gated on `loading` must be released instead of waiting forever.
   */
  const sessionFetchIsPending = computed(
    () => sessionFetchIsEnabled.value && !isSessionError.value && !feeSource.value,
  );

  const applicationFeeAmountInCents = computed(() => {
    if (!useCustomPriceFees.value) {
      return dollarsToCents(APPLICATION_FEE_DATA.amount);
    }

    return feeSource.value?.application_fee_in_cents ?? null;
  });

  const applicationFeeAmountInDollars = computed(() => {
    return toDollars(applicationFeeAmountInCents.value);
  });

  const confirmationDepositAmountInCents = computed(() => {
    if (!useCustomPriceFees.value) {
      return CONFIRMATION_PAYMENT_PRICE_IN_CENTS;
    }

    return feeSource.value?.confirmation_deposit_in_cents ?? null;
  });

  const confirmationDepositAmountInDollars = computed(() => toDollars(confirmationDepositAmountInCents.value));

  watch(isSessionError, (errorOccurred) => {
    if (errorOccurred) {
      toast.open({
        message: CUSTOMIZED_FEE_FETCH_ISSUE,
        type: "error",
        position: "bottom",
        duration: ERROR_TIMEOUT,
      });
    }
  });

  return {
    sessionConfirmationDeposit: {
      cents: confirmationDepositAmountInCents,
      dollars: confirmationDepositAmountInDollars,
    },
    sessionApplicationFee: {
      cents: applicationFeeAmountInCents,
      dollars: applicationFeeAmountInDollars,
    },
    feesAreResolved,
    loading: computed(() => isLoadingSession.value || sessionFetchIsPending.value),
    error: isSessionError,
  };
};
